import { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const STORAGE_KEY = 'apo_onboarding_done_v1';

/**
 * Onboarding tour triggered on first login. Persists completion in localStorage so the
 * merchant only sees it once. A "Start tour" button in Settings re-triggers it manually.
 */
export function OnboardingTour({ user }) {
  useEffect(() => {
    if (!user) return;
    const done = localStorage.getItem(STORAGE_KEY);
    if (done) return;

    // Wait for sidebar elements to mount
    const t = setTimeout(() => {
      startTour(() => localStorage.setItem(STORAGE_KEY, '1'));
    }, 1500);
    return () => clearTimeout(t);
  }, [user]);

  return null;
}

export function startTour(onDone) {
  const d = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayOpacity: 0.6,
    nextBtnText: 'Next →',
    prevBtnText: '← Back',
    doneBtnText: 'Got it',
    onDestroyed: () => { if (onDone) onDone(); },
    steps: [
      {
        element: '[data-testid="nav-dashboard"]',
        popover: {
          title: 'Welcome to Advanced Product Options',
          description: 'This quick tour will show you the core features. You can restart it anytime from Settings.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-testid="nav-products"]',
        popover: {
          title: 'Products',
          description: 'Import your Shopify products here. Attach option sets to products so custom options show on the storefront.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-option-sets"]',
        popover: {
          title: 'Option Sets',
          description: 'Create reusable option sets with dropdowns, swatches, inputs and more. Link them to products for cart-ready extras.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-variant-mapping"]',
        popover: {
          title: 'Variant Mapping (Premium)',
          description: 'Map combinations of options to real Shopify variants for accurate inventory tracking.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-bridge-products"]',
        popover: {
          title: 'Bridge Products',
          description: 'Shopify auto-creates hidden products to charge option add-ons. Manage them here.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-translations"]',
        popover: {
          title: 'Translations',
          description: 'Translate your option sets to multiple languages — manually or with AI. Syncs with your Shopify Markets.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-widget-style"]',
        popover: {
          title: 'Widget Style',
          description: 'Customize fonts, colors, border radius and see a live desktop/mobile preview of your storefront widget.',
          side: 'right',
        },
      },
      {
        element: '[data-testid="nav-settings"]',
        popover: {
          title: 'Settings',
          description: "You're all set! Head to Settings for app-wide preferences, or start building your first option set.",
          side: 'right',
        },
      },
    ],
  });
  d.drive();
}
